import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import {
  createListingLayoutLink,
  deleteListingLayoutLink,
  normalizeResolvedLayout,
  resolveLayoutFromUrl,
} from './analyticsListingApi';
import {
  buildLinksByCustomerRef,
  getReplaceCandidateForListingRef,
} from './listingLinkPreflight';
import { CLIENT_COPY_LISTING_PHRASE } from './signupFloorPlanOrigin';
import { ANALYTICS_FONT_FAMILY } from './analyticsTypography';
import {
  ANALYTICS_RED,
  analyticsGreenAlpha,
  analyticsRedAlpha,
} from './analyticsColors';
import {
  getListingInputModalDialogProps,
  LISTING_INPUT_MODAL_CONTENT_SX,
} from './listingInputModalStyles';
import {
  LISTING_ID_HELPER,
  LISTING_ID_LABEL,
  LISTING_ID_PLACEHOLDER,
  ListingModalSection,
  ListingModalTextField,
  ListingModalTitleBar,
  LISTING_MODAL_BODY_SX,
  LISTING_MODAL_HELPER_SX,
} from './listingModalUi';
import ListingStatusFields from './ListingStatusFields';
import { parseDateInput, requiresOffMarketDate } from './listingMarketDates';

const RESOLVE_DEBOUNCE_MS = 350;

/**
 * Link a floor plan to a listing ID by pasting the gallery URL (or the
 * unique ID at the end of it). Resolves the layout via the partner-scoped
 * `/listing-layout-links/resolve-layout` endpoint so admins can link plans
 * that are outside the current date range and therefore not visible in the
 * Floor plans library.
 */
const LinkByUrlModal = ({
  open,
  onClose,
  partner,
  existingLinks = [],
  planByCustomerRef = null,
  mlsSyncEnabled = false,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogProps = getListingInputModalDialogProps(fullScreenDialog);

  const [urlInput, setUrlInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [resolvedPlan, setResolvedPlan] = useState(null);
  const [listingId, setListingId] = useState('');
  const [customPropertyStatus, setCustomPropertyStatus] = useState('');
  const [onMarketDate, setOnMarketDate] = useState('');
  const [offMarketDate, setOffMarketDate] = useState('');
  const [backfillEvents, setBackfillEvents] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [replaceCandidate, setReplaceCandidate] = useState(null);

  const resolveReqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setUrlInput('');
    setResolving(false);
    setResolveError(null);
    setResolvedPlan(null);
    setListingId('');
    setCustomPropertyStatus('');
    setOnMarketDate('');
    setOffMarketDate('');
    setBackfillEvents(true);
    setSaving(false);
    setSaveError(null);
    setReplaceCandidate(null);
    resolveReqRef.current += 1;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setResolvedPlan(null);
      setResolveError(null);
      setResolving(false);
      return undefined;
    }
    const reqId = ++resolveReqRef.current;
    setResolving(true);
    setResolveError(null);

    const timer = setTimeout(async () => {
      try {
        const raw = await resolveLayoutFromUrl(partner, trimmed);
        if (reqId !== resolveReqRef.current) return;
        const normalized = normalizeResolvedLayout(raw);
        if (!normalized) {
          setResolvedPlan(null);
          setResolveError(
            'That link did not resolve to a floor plan. Paste the full gallery URL or the unique ID at the end of it.'
          );
        } else {
          setResolvedPlan(normalized);
        }
      } catch (e) {
        if (reqId !== resolveReqRef.current) return;
        setResolvedPlan(null);
        setResolveError(
          e?.message || 'Could not resolve that floor plan link. Try again.'
        );
      } finally {
        if (reqId === resolveReqRef.current) setResolving(false);
      }
    }, RESOLVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, urlInput, partner]);

  const linksByRef = useMemo(
    () => buildLinksByCustomerRef(existingLinks),
    [existingLinks]
  );

  const runCreate = async ({ ref, deletePriorLinkId = null }) => {
    if (!resolvedPlan?.uuid) {
      setSaveError('No floor plan selected.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (deletePriorLinkId) {
        await deleteListingLayoutLink(deletePriorLinkId, partner);
      }
      const onIso =
        !mlsSyncEnabled && onMarketDate.trim() ? parseDateInput(onMarketDate) : null;
      const offIso =
        !mlsSyncEnabled &&
        requiresOffMarketDate(customPropertyStatus) &&
        offMarketDate.trim()
          ? parseDateInput(offMarketDate)
          : null;
      const result = await createListingLayoutLink({
        customer_ref: ref,
        layoutUuid: resolvedPlan.uuid,
        partner,
        backfillEvents,
        customPropertyStatus: mlsSyncEnabled ? null : customPropertyStatus || null,
        onMarketDate: onIso,
        offMarketDate: offIso,
      });
      onSuccess?.(result);
      onClose();
    } catch (e) {
      const msg = e?.message || 'Something went wrong. Try again.';
      if (/409|already linked/i.test(msg)) {
        setSaveError(
          'This floor plan (or one of its copies) is already linked to that listing ID.'
        );
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ref = listingId.trim();
    if (!ref) {
      setSaveError('Enter your listing ID (for example from Luxury Presence).');
      return;
    }
    if (!resolvedPlan) {
      setSaveError('Paste a gallery URL or unique ID first.');
      return;
    }
    if (resolvedPlan.isFork) {
      setSaveError(
        `This is a client copy. Link the original upload instead — ${CLIENT_COPY_LISTING_PHRASE}`
      );
      return;
    }
    if (!mlsSyncEnabled) {
      if (onMarketDate.trim() && parseDateInput(onMarketDate) === undefined) {
        setSaveError('On-market date is not valid.');
        return;
      }
      if (requiresOffMarketDate(customPropertyStatus) && !offMarketDate.trim()) {
        setSaveError('Enter the off-market date for under contract or sold.');
        return;
      }
      if (
        requiresOffMarketDate(customPropertyStatus) &&
        offMarketDate.trim() &&
        parseDateInput(offMarketDate) === undefined
      ) {
        setSaveError('Off-market date is not valid.');
        return;
      }
    }

    const replace = getReplaceCandidateForListingRef({
      listingRef: ref,
      layoutUuid: resolvedPlan.uuid,
      linksByRef,
      planByCustomerRef,
    });
    if (replace) {
      setReplaceCandidate(replace);
      return;
    }

    await runCreate({ ref });
  };

  const handleConfirmReplace = async () => {
    if (!replaceCandidate) return;
    const { ref, priorLinkId } = replaceCandidate;
    setReplaceCandidate(null);
    await runCreate({ ref, deletePriorLinkId: priorLinkId });
  };

  if (replaceCandidate) {
    return (
      <Dialog
        open={open}
        onClose={saving ? undefined : () => setReplaceCandidate(null)}
        {...dialogProps}
      >
        <ListingModalTitleBar
          title="Replace existing link?"
          onClose={() => setReplaceCandidate(null)}
          onSave={handleConfirmReplace}
          saving={saving}
          saveAriaLabel="Replace link"
        />
        <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
          <Stack spacing={2.5}>
            <ListingModalSection>
              <Typography sx={{ ...LISTING_MODAL_BODY_SX, color: 'rgba(0,0,0,0.65)' }}>
                Listing {replaceCandidate.ref} is already linked to
                {replaceCandidate.priorPlanName ? ' ' : ' another floor plan.'}
                {replaceCandidate.priorPlanName && (
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {replaceCandidate.priorPlanName}
                  </Typography>
                )}
                {replaceCandidate.priorPlanName ? '.' : ''} Replacing will unlink the existing floor
                plan and link {resolvedPlan?.name || 'this floor plan'} instead.
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {replaceCandidate.priorPlanName && (
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.03)' }}
                  >
                    <Avatar
                      variant="rounded"
                      src={replaceCandidate.priorPlanThumb || undefined}
                      sx={{ width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }}
                    >
                      {replaceCandidate.priorPlanName?.[0] || 'F'}
                    </Avatar>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)' }} noWrap>
                      Currently linked: {replaceCandidate.priorPlanName}
                    </Typography>
                  </Stack>
                )}
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ p: 1, borderRadius: '10px', bgcolor: analyticsGreenAlpha(0.06) }}
                >
                  <Avatar
                    variant="rounded"
                    src={resolvedPlan?.thumbnail || undefined}
                    sx={{ width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }}
                  >
                    {resolvedPlan?.name?.[0] || 'F'}
                  </Avatar>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.78)' }} noWrap>
                    Link instead: {resolvedPlan?.name || 'this floor plan'}
                  </Typography>
                </Stack>
              </Stack>
            </ListingModalSection>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  const trimmedUrl = urlInput.trim();
  const showResolveIcon = trimmedUrl.length > 0 && !resolving;

  const canSave = Boolean(resolvedPlan && !resolvedPlan.isFork && listingId.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...dialogProps}>
      <ListingModalTitleBar
        title="Link by gallery URL"
        onClose={onClose}
        onSave={handleSave}
        saving={saving}
        saveDisabled={!canSave}
        saveAriaLabel="Link listing"
      />
      <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
        <Stack spacing={2.5}>
          <ListingModalSection label="Floor plan">
            <Typography sx={LISTING_MODAL_BODY_SX}>
              Paste the staged floor plan link (or the unique ID at the end of it). We will find the
              floor plan and let you link it to a listing ID, even if it was uploaded outside the date
              range above.
            </Typography>

            <ListingModalTextField
              label="Gallery URL or unique ID"
              placeholder="https://your-site.magicfurnish.com/floor-gallery/…"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              autoFocus
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {resolving && <CircularProgress size={16} thickness={5} />}
                    {showResolveIcon && resolvedPlan && !resolvedPlan.isFork && (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 18, color: analyticsGreenAlpha(0.85) }}
                      />
                    )}
                    {showResolveIcon && (resolveError || resolvedPlan?.isFork) && (
                      <ReportProblemOutlinedIcon
                        sx={{ fontSize: 18, color: analyticsRedAlpha(0.85) }}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            {resolveError && (
              <Typography sx={{ fontSize: '0.78rem', color: ANALYTICS_RED, lineHeight: 1.5 }}>
                {resolveError}
              </Typography>
            )}

            {resolvedPlan && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  p: 1.25,
                  borderRadius: '12px',
                  bgcolor: resolvedPlan.isFork
                    ? analyticsRedAlpha(0.04)
                    : 'rgba(0,0,0,0.03)',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Avatar
                  variant="rounded"
                  src={resolvedPlan.thumbnail || undefined}
                  sx={{ width: 56, height: 56, borderRadius: '8px', flexShrink: 0 }}
                >
                  {resolvedPlan.name?.[0] || 'F'}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }} noWrap>
                    {resolvedPlan.name}
                  </Typography>
                  {resolvedPlan.uniqueId && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'rgba(0,0,0,0.5)',
                        fontFamily: ANALYTICS_FONT_FAMILY,
                        mt: 0.25,
                      }}
                      noWrap
                    >
                      {resolvedPlan.uniqueId}
                    </Typography>
                  )}
                  {resolvedPlan.isFork && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#b45309', mt: 0.5, lineHeight: 1.4 }}>
                      This is a client copy. Find the original upload from the Floor plans library
                      and link that instead — {CLIENT_COPY_LISTING_PHRASE}
                    </Typography>
                  )}
                  {!resolvedPlan.isFork && resolvedPlan.customerRef && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#b45309', mt: 0.5, lineHeight: 1.4 }}>
                      Already linked to listing {resolvedPlan.customerRef}. Linking to a different
                      listing ID below will replace that link.
                    </Typography>
                  )}
                </Box>
              </Stack>
            )}
          </ListingModalSection>

          <ListingModalSection label="Listing">
            <ListingModalTextField
              label={LISTING_ID_LABEL}
              placeholder={LISTING_ID_PLACEHOLDER}
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              disabled={!resolvedPlan || resolvedPlan.isFork}
            />
            <Typography sx={{ ...LISTING_MODAL_HELPER_SX, mt: 0 }}>{LISTING_ID_HELPER}</Typography>

            <ListingStatusFields
              theme={theme}
              mlsSyncEnabled={mlsSyncEnabled}
              customPropertyStatus={customPropertyStatus}
              onCustomPropertyStatusChange={setCustomPropertyStatus}
              onMarketDate={onMarketDate}
              onOffMarketDate={offMarketDate}
              onOnMarketDateChange={setOnMarketDate}
              onOffMarketDateChange={setOffMarketDate}
              disabled={saving || !resolvedPlan || resolvedPlan.isFork}
              showDaysPreview
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={backfillEvents}
                  onChange={(e) => setBackfillEvents(e.target.checked)}
                  size="small"
                  disabled={!resolvedPlan || resolvedPlan.isFork}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>
                  Update past activity for this floor plan
                </Typography>
              }
            />
          </ListingModalSection>

          {saveError && (
            <Typography sx={{ fontSize: '0.78rem', color: ANALYTICS_RED, px: 0.25 }}>
              {saveError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default LinkByUrlModal;
